import moment from 'moment-timezone';
import queryString from 'query-string';

import { APIError } from '@/core/api';
import ERROR_CODES from '@/core/apiErrorCodes';
import { getCache, setCache } from '@/utils';

const key = 'invite';

function isValid(val: any) {
  return val && val.invite && val.expires && moment().isBefore(moment(val.expires));
}

export function saveInvitationToken() {
  const query = queryString.parse(window.location.search);

  if (query.invite) {
    return setCache(key, {
      invite: query.invite,
      expires: moment().add(12, 'hour').toISOString(),
    });
  }

  const val = getCache(key);
  if (!isValid(val)) return clearInvitationToken();
}

export function getInvitationToken() {
  const val = getCache(key);
  if (isValid(val)) return val.invite;
}

export function clearInvitationToken() {
  setCache(key, undefined);
}

// ENG-6105: a login that could not accept the cached invitation still reloads
// the page, so this notice waits for the next boot (core/postLoginNotice).
export const INVITATION_NOT_ACCEPTED_NOTICE =
  "We couldn't add you to the project; ask for a new invitation.";

// The api's AcceptInvite answers an expired or invalid token with these
// messages under the generic unknown.unknown code (invitation_service.go), so
// the message is the only signal. No retry can succeed after any of them.
const TERMINAL_INVITATION_MESSAGES = new Set([
  'invitation token expired',
  'invalid invitation token',
]);

/**
 * True when the api refused the invitation for good. Anything else (a network
 * failure, a 5xx, an unexpected resolver error) may pass on a later try.
 */
export function isTerminalInvitationError(err: unknown): boolean {
  if (!(err instanceof APIError)) return false;
  return err.rawError.some(
    (e) =>
      e.extensions?.code === ERROR_CODES.INVITATION_ACTIVE_INVITATION_NOT_FOUND ||
      (e.extensions?.code === ERROR_CODES.UNKNOWN && TERMINAL_INVITATION_MESSAGES.has(e.message))
  );
}

/**
 * After an acceptInvitation for `token`: forget the cached invitation when it
 * is the same one and the outcome is final (accepted, or refused for good).
 * Pass the error for a failure; a transient one keeps the cache for a retry.
 */
export function settleCachedInvitation(token: string, err?: unknown): void {
  if (getInvitationToken() !== token) return;
  if (err === undefined || isTerminalInvitationError(err)) clearInvitationToken();
}
